import i18n from "../../../i18n";
import React from "react";
import { Text, Badge, Stack, Group, Table } from "@mantine/core";
import EntityTabAnchor from "./EntityTabAnchor";
import { isPoolReleasedPayrollBid, formatPayrollBidDate } from "./payrollSourceHelpers";
import { formatCommissionCategory } from "../../../helpers/payrollBidDisplay";
import { incentiveTypeBadge } from "../../../data/options";

const PayrollBidRowContent = ({ item, formatter, payrollId }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "SETTLED":
        return "green";
      case "HOLD":
        return "yellow";
      case "PENDING":
      default:
        return "gray";
    }
  };

  const getSourceLabel = (bid) => {
    if (bid.sourceType === "ADJUSTMENT") return "Adjustment";
    if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote)
      return "Client Payment";
    if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order)
      return "Comm. Statement";
    if (bid.sourceType === "INCENTIVE" || bid.incentive)
      return incentiveTypeBadge(bid.incentive?.incentiveType).label;
    return bid.sourceType;
  };

  const getSourceColor = (bid) => {
    if (bid.sourceType === "ADJUSTMENT") return "red";
    if (bid.sourceType === "CLIENTPAYMENT" || bid.debitNote) return "blue";
    if (bid.sourceType === "COMMISSIONSTATEMENT" || bid.order) return "violet";
    if (bid.sourceType === "INCENTIVE" || bid.incentive)
      return incentiveTypeBadge(bid.incentive?.incentiveType).color;
    return "gray";
  };

  const getClient = (bid) => {
    if (bid.sourceType === "INCENTIVE" && bid.incentive?.consultant) {
      return null;
    }
    return bid.debitNote?.client || bid.order?.client;
  };

  const getProduct = (bid) => {
    return (
      bid.debitNote?.product ||
      bid.debitNote?.order?.product ||
      bid.order?.product ||
      bid.debitNote?.productType ||
      bid.debitNote?.order?.productType ||
      bid.order?.productType
    );
  };

  const getSourceCode = (bid) => {
    if (bid.sourceType === "CLIENTPAYMENT" || bid.sourceType === "ADJUSTMENT") {
      return bid.debitNote?.code;
    } else if (bid.sourceType === "COMMISSIONSTATEMENT") {
      return bid.order?.code;
    } else if (bid.sourceType === "INCENTIVE") {
      return bid.incentive?.code;
    }
    return null;
  };

  return (
    <Table fontSize="xs" striped highlightOnHover withBorder mt="md">
      <thead>
        <tr>
          <th style={{ width: 40, textAlign: "center" }}>#</th>
          <th style={{ minWidth: 200 }}>Bid</th>
          <th style={{ minWidth: 100 }}>{i18n.t("Order")}</th>
          <th style={{ minWidth: 100 }}>{i18n.t("Debit Note")}</th>
          <th style={{ minWidth: 130 }}>{i18n.t("Client")}</th>
          <th style={{ minWidth: 130 }}>{i18n.t("Product")}</th>
          <th style={{ minWidth: 120 }}>{i18n.t("Commission Category")}</th>
          <th style={{ width: 120, textAlign: "right" }}>{i18n.t("Amount")}</th>
        </tr>
      </thead>
      <tbody>
        {item.bids.map((bid, index) => {
          const client = getClient(bid);
          const product = getProduct(bid);
          const isFromPool = isPoolReleasedPayrollBid(bid, payrollId);
          const originalPayrollCode =
            bid.originalPayroll?.code ||
            (typeof bid.originalPayroll === "object"
              ? null
              : bid.originalPayroll);
          return (
            <tr key={bid._id || index}>
              <td style={{ textAlign: "center" }}>
                <Text size="xs" color="dimmed">
                  {index + 1}
                </Text>
              </td>
              <td>
                <>
                  <Group noWrap spacing={1}>
                    <Badge
                      size="xs"
                      color={getStatusColor(bid.status)}
                      variant="filled"
                      radius="0"
                    >
                      {bid.status}
                    </Badge>
                    <Badge
                      size="xs"
                      color={getSourceColor(bid)}
                      variant="light"
                      radius="0"
                    >
                      {getSourceLabel(bid)}
                    </Badge>
                    {isFromPool && (
                      <Badge size="xs" color="teal" variant="light" radius="0">
                        From Pool
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" weight={500}>
                    {bid.code}
                  </Text>
                  {isFromPool && originalPayrollCode && (
                    <Text size="xs" color="dimmed">
                      From {originalPayrollCode}
                    </Text>
                  )}
                  {isFromPool && (bid.heldAt || bid.releasedFromPoolAt) && (
                    <Text size="xs" color="dimmed">
                      {bid.heldAt && `Held ${formatPayrollBidDate(bid.heldAt)}`}
                      {bid.heldAt && bid.releasedFromPoolAt && " · "}
                      {bid.releasedFromPoolAt &&
                        `Released ${formatPayrollBidDate(bid.releasedFromPoolAt)}`}
                    </Text>
                  )}
                </>
              </td>
              <td>
                <EntityTabAnchor
                  entity="order"
                  id={bid.order?._id || bid.order}
                  label={bid.order?.code}
                />
              </td>
              <td>
                <EntityTabAnchor
                  entity="debitNote"
                  id={bid.debitNote?._id || bid.debitNote}
                  label={bid.debitNote?.code}
                />
              </td>
              <td>
                {client ? (
                  <Stack spacing={0}>
                    <Text size="xs" weight={500} lineClamp={1}>
                      {client.name || client.code}
                    </Text>
                    {client.cname && (
                      <Text size="xs" color="dimmed" lineClamp={1}>
                        {client.cname}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                {product ? (
                  <Stack spacing={0}>
                    <Text size="xs" weight={500} lineClamp={1}>
                      {product.name || product.code}
                    </Text>
                    {product.cname && (
                      <Text size="xs" color="dimmed" lineClamp={1}>
                        {product.cname}
                      </Text>
                    )}
                  </Stack>
                ) : (
                  <Text size="xs" color="dimmed">
                    -
                  </Text>
                )}
              </td>
              <td>
                <Text size="xs">
                  {formatCommissionCategory(bid.commissionCategory, i18n.t) ||
                    "-"}
                </Text>
              </td>
              <td style={{ textAlign: "right" }}>
                <Text
                  size="xs"
                  weight={600}
                  color={bid.commissionAmount >= 0 ? "teal" : "red"}
                >
                  {formatter.currency(bid.commissionAmount)}
                </Text>
              </td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

export default PayrollBidRowContent;
